import { Label } from "@radix-ui/react-dropdown-menu";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import { toast } from "components/ui/use-toast";
import { X } from "lucide-react";
import React, { useState } from "react";
import { useSpedActions } from "../ducks/hooks/actions";
import { useSpedState } from "../ducks/hooks/state";
import { useUploadSpedFilesMutation } from "@/services/sped-file";
import SelectFilters from "../components/select-filters";
import { useFiltersState } from "../ducks/filter/hooks/state";
import StoreFilter from "../components/store-filter";
import { useFilterState } from "@/pages/conciliation/stores/ducks/filter/hooks/filterState";
import { ErrorResponse } from "@/@types/tax-file";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";


export const Sped: React.FC = () => {
  const { singleFile, processedFile } = useSpedState();
  const { updateSingleFile, clearAllFiles, updateProcessedFile } =
    useSpedActions();
  const [uploadSpedFiles, { isLoading }] = useUploadSpedFilesMutation();
  const [resetInput, setResetInput] = useState(0);
  const { fromDate, toDate } = useFiltersState();
  const filterState = useFilterState();

  const { filters } = filterState || { filters: {} };
  const { code } = filters;
  const handleSingleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.type !== "text/plain") {
        toast({
          variant: "destructive",
          description: "O arquivo único deve ser um arquivo .txt",
          duration: 5000,
        });
      } else {
        updateSingleFile(file);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    clearAllFiles();

    if (!singleFile) {
      toast({
        variant: "destructive",
        description: "Por favor, selecione um arquivo SPED.",
        duration: 5000,
      });
      return;
    }

    if (!fromDate || !toDate) {
      toast({
        variant: "destructive",
        description: "Por favor, selecione um intervalo de datas.",
        duration: 5000,
      });
      return;
    }

    if (!code) {
      toast({
        variant: "destructive",
        description: "Por favor, selecione uma Loja",
        duration: 5000,
      });
      return;
    }

    const formData = new FormData();
    formData.append("files", singleFile);

    const params = {
      startDate: fromDate.toISOString().split("T")[0],
      endDate: toDate.toISOString().split("T")[0],
      storeId: Number(code),
    };

    try {
      const result = await uploadSpedFiles({ params, body: formData }).unwrap();
      toast({
        variant: "default",
        description: result.message,
        duration: 5000,
      });

      if (result.content) {
        updateProcessedFile(result.content);
      }
    } catch (err: unknown) {
      const error = err as ErrorResponse;
      const message = error?.data?.messages?.[0];
      toast({
        variant: "destructive",
        description: message,
        duration: 5000,
      });
    } finally {
      clearAllFiles();
      updateSingleFile(null);
      setResetInput((prev) => prev + 1);
    }
  };

  const getDownloadLink = () => {
    if (!processedFile) return null;
    const blob = new Blob([processedFile], { type: "text/plain" });

    return URL.createObjectURL(blob);
  };

  const downloadLink = getDownloadLink();

  const handleRemoveSingleFile = () => {
    updateSingleFile(null);
  };

  return (
    <PageLayout>
      <PageTitle title="Fiscal" description="Geração do Arquivo Sped" />
      <ContentContainer>
        <form
          onSubmit={handleSubmit}
          className="space-y-4 w-full"
        >
          <div className="flex space-x-4">
            <div className="flex-grow">
              <SelectFilters />
            </div>
            <div className="flex-grow">
              <StoreFilter />
            </div>
          </div>

          <div className="flex flex-col space-y-2">
            <Label>Arquivo Sped</Label>
            <div className="flex items-center justify-center w-full h-8 border rounded-md bg-white">
              <label
                htmlFor="single-file"
                className="w-full h-full flex items-center justify-center cursor-pointer text-sm text-gray-500"
              >
                {singleFile ? singleFile.name : "Selecione o arquivo"}
              </label>
              <Input
                id="single-file"
                type="file"
                accept=".txt"
                disabled={isLoading}
                onChange={handleSingleFileChange}
                className="hidden"
                key={`single-${resetInput}`}
              />
            </div>

            {singleFile && (
              <Card>
                <CardContent className="flex items-center justify-between p-4">
                  <span className="text-sm truncate">{singleFile?.name}</span>
                  <X
                    onClick={handleRemoveSingleFile}
                    className="h-4 w-4 cursor-pointer"
                  />
                </CardContent>
              </Card>
            )}
          </div>

          <div className="flex space-x-2 justify-end">
            {processedFile && (
              <a href={downloadLink || undefined} download="sped_atualizado.txt">
                <Button type="button" className="h-8">
                  Baixar
                </Button>
              </a>
            )}
            <Button
              type="submit"
              isLoading={isLoading}
              className="h-8"
            >
              Processar arquivo
            </Button>
          </div>
        </form>
      </ContentContainer>
    </PageLayout>
  );
};
